import { ChangeEvent, ReactNode, useCallback } from 'react';

import { useSnapshot } from '@pukka/valtio';
import debounce from 'lodash/debounce';
import { twMerge } from 'tailwind-merge';

import { SearchInput, Select } from '../../Fields';
import { useSelectModalStateContext } from '../SelectModal.state';

interface SelectModalFilterCommonProps {
  readonly placeholder?: string;
  readonly children?: ReactNode;
  readonly className?: string;
}

interface SelectModalFilterWithoutCategoryProps {
  readonly categories?: never;
  readonly category?: never;
  readonly onChange?: (keyword: string) => void;
}

interface SelectModalFilterWithCategoryProps<T extends string> {
  readonly categories: T[];
  readonly category?: T;
  readonly onChange?: (keyword: string, category?: T) => void;
}

type SelectModalFilterProps<T extends string> = SelectModalFilterCommonProps &
  (SelectModalFilterWithCategoryProps<T> | SelectModalFilterWithoutCategoryProps);

const debouncedOnChange = debounce(function applier(fn: () => void) {
  fn();
}, 300);

function SelectModalFilter<U, T extends string>({
  categories,
  category,
  placeholder = 'Search',
  children,
  onChange,
  className,
}: SelectModalFilterProps<T>) {
  const state = useSelectModalStateContext<U>();
  const { onChangeFilter } = state;
  const { filter } = useSnapshot(state);

  const onChangeKeyword = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget;

      onChangeFilter(value);
      debouncedOnChange(() => {
        onChange?.(value, category);
      });
    },
    [onChange, onChangeFilter, category],
  );

  const onChangeCategory = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      onChange?.(filter.keyword, e.currentTarget.value as T);
    },
    [onChange, filter.keyword],
  );

  return (
    <div className={twMerge('flex flex-row gap-x-2', className)}>
      <SearchInput value={filter.keyword} placeholder={placeholder} onChange={onChangeKeyword} className="w-full text-xs" />
      {categories ? (
        <Select value={category as string} onChange={onChangeCategory} className="w-48">
          {categories.map((c) => {
            return <option key={c}>{c}</option>;
          })}
        </Select>
      ) : null}
      {children}
    </div>
  );
}

export { SelectModalFilter };
